import React, { useState, useEffect, useRef } from "react";
import {
  SearchNormal1, Add, Edit2, Trash, ArrowLeft2, ArrowRight2,
  People, Map1, Link2, Location,
} from "iconsax-reactjs";
import { db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  onSnapshot,
  doc,
  setDoc,
  serverTimestamp,
  query,
  where
} from "firebase/firestore";
import { encodeGeohash } from "../utils/geohash";
import Select from "./Select";
import { hasPinnedCoordinates } from "./listing/listingFormModel";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./DealersPanel.css";

// Fix default marker icon issue in Leaflet with Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const ITEMS_PER_PAGE = 12;

// Page numbers to show: first, last, and the current page's neighbours,
// with null marking a gap so long lists stay one row.
function pageList(total, current) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  return sorted.reduce((out, p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push(null);
    out.push(p);
    return out;
  }, []);
}

export default function DealersPanel() {
  const [dealers, setDealers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDealer, setEditingDealer] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Form states
  const [name, setName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [locality, setLocality] = useState("");
  const [city, setCity] = useState("");
  const [status, setStatus] = useState("active");
  const [googleMapsUrl, setGoogleMapsUrl] = useState("");
  const [mapSearchQuery, setMapSearchQuery] = useState("");
  const [locationMethod, setLocationMethod] = useState("map");
  const [mapLat, setMapLat] = useState(12.9716);
  const [mapLng, setMapLng] = useState(77.5946);
  // Whether the office has actually been pinned. The map opens on a default
  // point, and saving it untouched put dealers in central Bengaluru.
  const [pinSet, setPinSet] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // Fetch dealers on mount
  useEffect(() => {
    const usersCol = collection(db, "users");
    const q = query(usersCol, where("role", "==", "dealer"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });
      // Sort in memory by createdAt descending
      list.sort((a, b) => {
        const timeA = a.createdAt?.seconds || a.createdAt?.toMillis?.() || 0;
        const timeB = b.createdAt?.seconds || b.createdAt?.toMillis?.() || 0;
        return timeB - timeA;
      });
      setDealers(list);
      setLoaded(true);
    }, (error) => {
      console.error("Failed to fetch dealers:", error);
      setLoaded(true);
    });

    return () => unsubscribe();
  }, []);

  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18`);
      const data = await res.json();
      if (data && data.address) {
        const addr = data.address;
        const resolvedLocality = addr.suburb || addr.neighbourhood || addr.village || addr.city_district || "";
        const resolvedCity = addr.city || addr.town || addr.county || "";
        const street = addr.road || addr.subdivision || "";
        const houseNumber = addr.house_number || "";
        const resolvedAddress = [houseNumber, street].filter(Boolean).join(", ") || data.display_name.split(",")[0] || "";

        if (resolvedLocality) setLocality(resolvedLocality);
        if (resolvedCity) setCity(resolvedCity);
        if (resolvedAddress) setAddress(resolvedAddress);
      }
    } catch (error) {
      console.warn("Reverse geocoding failed:", error);
    }
  };

  const parseGoogleMapsUrl = (url) => {
    try {
      const matchAt = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (matchAt) {
        return { lat: parseFloat(matchAt[1]), lng: parseFloat(matchAt[2]) };
      }
      const matchQ = url.match(/[?&](q|ll|query)=(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (matchQ) {
        return { lat: parseFloat(matchQ[2]), lng: parseFloat(matchQ[3]) };
      }
      const matchSearch = url.match(/\/search\/(-?\d+\.\d+),\+?(-?\d+\.\d+)/);
      if (matchSearch) {
        return { lat: parseFloat(matchSearch[1]), lng: parseFloat(matchSearch[2]) };
      }
    } catch (e) {
      console.warn("Failed to parse Google Maps URL:", e);
    }
    return null;
  };

  const handleGoogleMapsUrlChange = async (url) => {
    setGoogleMapsUrl(url);
    if (!url.trim()) return;

    let targetUrl = url;
    if (url.includes("maps.app.goo.gl")) {
      try {
        const res = await fetch(`https://api.codetabs.com/v1/proxy/?quest=${encodeURIComponent(url)}`);
        const text = await res.text();
        const matchUrl = text.match(/https:\/\/www\.google\.com\/maps\/[^\s"<>\b]+/);
        if (matchUrl) {
          targetUrl = matchUrl[0].replace(/&amp;/g, "&");
        }
      } catch (err) {
        console.warn("Failed to resolve short URL via proxy:", err);
      }
    }

    const coords = parseGoogleMapsUrl(targetUrl);
    if (coords) {
      setMapLat(coords.lat);
      setMapLng(coords.lng);
      setPinSet(true);

      if (mapRef.current && markerRef.current) {
        mapRef.current.setView([coords.lat, coords.lng], 15);
        markerRef.current.setLatLng([coords.lat, coords.lng]);
      }
      await reverseGeocode(coords.lat, coords.lng);
    }
  };

  const handleMapSearch = async (e) => {
    if (e) e.preventDefault();
    if (!mapSearchQuery.trim()) return;

    try {
      const queryStr = encodeURIComponent(mapSearchQuery);
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${queryStr}&limit=1`);
      const data = await res.json();
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        setMapLat(lat);
        setMapLng(lon);
        setPinSet(true);

        if (mapRef.current && markerRef.current) {
          mapRef.current.setView([lat, lon], 14);
          markerRef.current.setLatLng([lat, lon]);
        }
        await reverseGeocode(lat, lon);
      } else {
        alert("Location not found on map search.");
      }
    } catch (err) {
      console.error("Map search query failed:", err);
    }
  };

  // Map initialization inside the modal
  useEffect(() => {
    if (!isModalOpen || locationMethod !== "map") {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
      return;
    }

    const initialLat = editingDealer ? (editingDealer.latitude || 12.9716) : 12.9716;
    const initialLng = editingDealer ? (editingDealer.longitude || 77.5946) : 77.5946;

    const timer = setTimeout(() => {
      const container = document.getElementById("dealer-admin-map");
      if (!container || mapRef.current) return;

      const map = L.map("dealer-admin-map").setView([initialLat, initialLng], 13);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      }).addTo(map);

      const marker = L.marker([initialLat, initialLng], { draggable: true }).addTo(map);
      markerRef.current = marker;
      mapRef.current = map;

      marker.on("dragend", async () => {
        const position = marker.getLatLng();
        setMapLat(position.lat);
        setMapLng(position.lng);
        setPinSet(true);
        await reverseGeocode(position.lat, position.lng);
      });

      map.on("click", async (e) => {
        marker.setLatLng(e.latlng);
        setMapLat(e.latlng.lat);
        setMapLng(e.latlng.lng);
        setPinSet(true);
        await reverseGeocode(e.latlng.lat, e.latlng.lng);
      });
    }, 300);

    return () => {
      clearTimeout(timer);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, [isModalOpen, editingDealer, locationMethod]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name || !contactNumber || !address || !locality || !city) {
      alert("Please fill all required fields.");
      return;
    }
    if (!pinSet) {
      alert("Pin the dealer's office on the map, or paste a Google Maps link that includes coordinates.");
      return;
    }

    setIsSaving(true);
    try {
      const latitude = mapLat;
      const longitude = mapLng;
      // The state, when the address geocodes. Never a guessed default: a
      // failed lookup used to stamp every dealer "Karnataka".
      let stateName = editingDealer?.state || "";
      try {
        const queryStr = encodeURIComponent(`${address}, ${locality}, ${city}`);
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${queryStr}&limit=1`);
        const data = await res.json();
        if (data && data.length > 0) {
          const addrParts = (data[0].display_name || "").split(",");
          if (addrParts.length > 2) {
            stateName = addrParts[addrParts.length - 3].trim();
          }
        }
      } catch (err) {
        console.warn("State geocoding failed:", err);
      }

      if (editingDealer) {
        await setDoc(doc(db, "users", editingDealer.id), {
          id: editingDealer.id,
          role: "dealer",
          name,
          contactNumber,
          email: email || "",
          address,
          locality,
          city,
          state: stateName,
          latitude,
          longitude,
          geohash: encodeGeohash(latitude, longitude, 9),
          status,
          googleMapsUrl,
          createdAt: editingDealer.createdAt || serverTimestamp(),
        }, { merge: true });
      } else {
        // Created server side, under the account the dealer's phone number
        // signs in to (createDealerAccount). A profile written here under a
        // random id was a dealer nobody could ever sign in as.
        await httpsCallable(functions, "createDealerAccount")({
          phone: contactNumber, name, email, address, locality, city,
          state: stateName, latitude, longitude, status,
        });
      }

      setIsModalOpen(false);
      resetForm();
      setEditingDealer(null);
    } catch (err) {
      console.error("Failed to save dealer:", err);
      alert(err?.code?.startsWith("functions/") && err.message ? err.message : "Couldn't save this dealer. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setName("");
    setContactNumber("");
    setEmail("");
    setAddress("");
    setLocality("");
    setCity("");
    setStatus("active");
    setGoogleMapsUrl("");
    setMapSearchQuery("");
    setLocationMethod("map");
    setMapLat(12.9716);
    setMapLng(77.5946);
    setPinSet(false);
  };

  const handleEditPress = (dealer) => {
    setEditingDealer(dealer);
    setName(dealer.name || "");
    setContactNumber(dealer.contactNumber || "");
    setEmail(dealer.email || "");
    setAddress(dealer.address || "");
    setLocality(dealer.locality || "");
    setCity(dealer.city || "");
    setStatus(dealer.status || "active");
    setGoogleMapsUrl(dealer.googleMapsUrl || "");
    setMapLat(dealer.latitude || 12.9716);
    setMapLng(dealer.longitude || 77.5946);
    setPinSet(hasPinnedCoordinates(dealer.latitude, dealer.longitude));
    setLocationMethod(dealer.googleMapsUrl ? "url" : "map");
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingDealer(null);
    resetForm();
  };

  // Deletes the dealer's whole account server side, as the Users panel does
  // (deleteUserAccount): sign-in, profile, listings, reels, leads and chats.
  // Deleting only the profile doc left the rest live and orphaned.
  const handleDelete = async (dealer) => {
    const label = dealer.name || "this dealer";
    if (!window.confirm(`Delete ${label}'s account? Their listings, reels, leads and chats are deleted too. This can't be undone.`)) return;
    try {
      await httpsCallable(functions, "deleteUserAccount")({ uid: dealer.id });
    } catch (err) {
      console.error("Failed to delete dealer:", err);
      alert(err?.message || "Couldn't delete this dealer. Please try again.");
    }
  };

  const filteredDealers = dealers.filter((d) => {
    const matchesSearch =
      d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.locality?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.contactNumber?.includes(searchQuery);

    const matchesStatus = statusFilter === "all" || d.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter]);

  const totalPages = Math.ceil(filteredDealers.length / ITEMS_PER_PAGE);
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedDealers = filteredDealers.slice(startIdx, startIdx + ITEMS_PER_PAGE);

  const hasFilters = !!searchQuery.trim() || statusFilter !== "all";
  const clearFilters = () => { setSearchQuery(""); setStatusFilter("all"); };
  const statValue = (n) => (loaded ? n : <span className="skeleton dp-sk-stat" aria-hidden="true" />);
  const openCreate = () => { setEditingDealer(null); resetForm(); setIsModalOpen(true); };

  return (
    <div className="dp">
      <div className="stat-strip">
        <div className="stat">
          <span className="stat-label">Total dealers</span>
          <span className="stat-value">{statValue(dealers.length)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Active</span>
          <span className="stat-value">{statValue(dealers.filter(d => d.status === "active").length)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Inactive</span>
          <span className="stat-value">{statValue(dealers.filter(d => d.status === "inactive").length)}</span>
        </div>
      </div>

      <div className="toolbar dp-toolbar">
        <div className="search-field dp-search">
          <SearchNormal1 size={16} variant="Linear" aria-hidden="true" />
          <input
            type="text"
            className="input"
            placeholder="Search name, address, locality or phone"
            aria-label="Search dealers"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="segmented" role="group" aria-label="Dealer status">
          {[
            { key: "all", label: "All" },
            { key: "active", label: "Active" },
            { key: "inactive", label: "Inactive" }
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              aria-pressed={statusFilter === item.key}
              onClick={() => setStatusFilter(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="toolbar-spacer" />
        <div className="dp-toolbar-actions">
          <button type="button" className="btn-primary dp-add" onClick={openCreate}>
            <Add size={18} variant="Linear" aria-hidden="true" />
            Add dealer
          </button>

        </div>
      </div>

      {loaded && paginatedDealers.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <span className="empty-state-icon"><People size={22} variant="Linear" aria-hidden="true" /></span>
            {dealers.length === 0 ? (
              <>
                <h3>No dealers yet</h3>
                <p>Dealer accounts you add appear here with their contact details and status.</p>
              </>
            ) : (
              <>
                <h3>No dealers match</h3>
                <p>Nothing matches this search and status. Try another name, address, locality or phone number.</p>
                {hasFilters && <button type="button" className="btn-secondary btn-sm" onClick={clearFilters}>Clear filters</button>}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="table-wrap dp-table-wrap">
          <table className="table dp-table">
            <thead>
              <tr>
                <th scope="col">Dealer</th>
                <th scope="col">Phone</th>
                <th scope="col" className="dp-col-email">Email</th>
                <th scope="col">Location</th>
                <th scope="col">Status</th>
                <th scope="col"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody aria-busy={!loaded}>
              {!loaded && Array.from({ length: 5 }, (_, i) => (
                <tr key={`sk-${i}`} className="dp-row-skeleton" aria-hidden="true">
                  <td className="dp-c-dealer">
                    <div className="dp-dealer">
                      <span className="skeleton dp-sk-avatar" />
                      <span className="skeleton dp-sk-line" />
                    </div>
                  </td>
                  <td className="dp-c-phone"><span className="skeleton dp-sk-text" /></td>
                  <td className="dp-col-email dp-c-email"><span className="skeleton dp-sk-text" /></td>
                  <td className="dp-c-loc"><span className="skeleton dp-sk-text" /></td>
                  <td className="dp-c-status"><span className="skeleton dp-sk-chip" /></td>
                  <td className="dp-c-actions" />
                </tr>
              ))}
              {loaded && paginatedDealers.map((d) => {
                const dealerName = d.name || "this dealer";
                const place = [d.locality, d.city].filter(Boolean).join(", ") || d.address;
                return (
                  <tr key={d.id}>
                    <td className="dp-c-dealer">
                      <div className="dp-dealer">
                        <span className="avatar" aria-hidden="true">{(d.name || "D").trim().charAt(0).toUpperCase()}</span>
                        <span className="dp-dealer-name" title={d.name}>{d.name || "Unnamed dealer"}</span>
                      </div>
                    </td>
                    <td className="dp-c-phone">
                      {d.contactNumber ? <span className="dp-phone">{d.contactNumber}</span> : <span className="muted">No phone</span>}
                    </td>
                    <td className="dp-col-email dp-c-email">
                      {d.email ? <span className="dp-email" title={d.email}>{d.email}</span> : <span className="muted">No email</span>}
                    </td>
                    <td className="dp-c-loc">
                      {place ? <span className="dp-loc" title={d.address || place}>{place}</span> : <span className="muted">No address</span>}
                    </td>
                    <td className="dp-c-status">
                      <span className={`chip chip-dot ${d.status === "active" ? "chip-success" : ""}`}>
                        {d.status === "active" ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="dp-c-actions">
                      <div className="dp-actions">
                        <button type="button" className="btn-icon" onClick={() => handleEditPress(d)} title="Edit" aria-label={`Edit ${dealerName}`}>
                          <Edit2 size={18} variant="Linear" />
                        </button>
                        <button type="button" className="btn-icon dp-action-danger" onClick={() => handleDelete(d)} title="Delete" aria-label={`Delete ${dealerName}`}>
                          <Trash size={18} variant="Linear" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!loaded && <span className="sr-only" role="status">Loading dealers</span>}
        </div>
      )}

      {loaded && filteredDealers.length > 0 && (
        <nav className="pagination" aria-label="Dealer pages">
          <span>
            Showing <strong className="dp-page-range">{startIdx + 1} to {Math.min(startIdx + ITEMS_PER_PAGE, filteredDealers.length)}</strong> of {filteredDealers.length} {filteredDealers.length === 1 ? "dealer" : "dealers"}
          </span>
          {totalPages > 1 && (
            <div className="pagination-pages">
              <button type="button" className="page-btn" aria-label="Previous page" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                <ArrowLeft2 size={16} variant="Linear" />
              </button>
              {pageList(totalPages, currentPage).map((pg, i) => (
                pg === null ? (
                  <span key={`gap-${i}`} className="dp-page-gap" aria-hidden="true">…</span>
                ) : (
                  <button
                    key={pg}
                    type="button"
                    className={`page-btn ${pg === currentPage ? "active" : ""}`}
                    aria-current={pg === currentPage ? "page" : undefined}
                    aria-label={`Page ${pg}`}
                    onClick={() => setCurrentPage(pg)}
                  >
                    {pg}
                  </button>
                )
              ))}
              <button type="button" className="page-btn" aria-label="Next page" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                <ArrowRight2 size={16} variant="Linear" />
              </button>
            </div>
          )}
        </nav>
      )}

      {/* Add / edit dealer */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal dp-modal" role="dialog" aria-modal="true" aria-labelledby="dp-modal-title">
            <div className="modal-header">
              <h2 id="dp-modal-title">{editingDealer ? "Edit dealer" : "Add dealer"}</h2>
              <button type="button" className="btn-icon" aria-label="Close" onClick={handleCloseModal}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="18" y1="6" x2="6" y2="18" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSave} className="dp-form">
              <div className="modal-body dp-form-body">
                <div className="field">
                  <label className="field-label" htmlFor="dp-name">Dealer name</label>
                  <input
                    id="dp-name"
                    type="text"
                    className="input"
                    placeholder="e.g. Apex Realty Group"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div className="dp-field-row">
                  <div className="field">
                    <label className="field-label" htmlFor="dp-phone">Contact number</label>
                    <input
                      id="dp-phone"
                      type="text"
                      className="input"
                      placeholder="e.g. +91 98765 43210"
                      value={contactNumber}
                      onChange={(e) => setContactNumber(e.target.value)}
                      required
                    />
                  </div>
                  <div className="field">
                    <label className="field-label" htmlFor="dp-email">Email <span className="dp-optional">(optional)</span></label>
                    <input
                      id="dp-email"
                      type="email"
                      className="input"
                      placeholder="e.g. info@apexrealty.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                <div className="field">
                  <label className="field-label" htmlFor="dp-status">Status</label>
                  <Select
                    id="dp-status"
                    value={status}
                    onChange={setStatus}
                    options={[
                      { value: "active", label: "Active" },
                      { value: "inactive", label: "Inactive" },
                    ]}
                  />
                </div>

                <section className="dp-section" aria-labelledby="dp-location-title">
                  <h3 id="dp-location-title" className="dp-section-title">Location</h3>
                  <div className="segmented dp-method" role="group" aria-label="How to set the location">
                    <button type="button" aria-pressed={locationMethod === "map"} onClick={() => setLocationMethod("map")}>
                      <Map1 size={16} variant="Linear" aria-hidden="true" />
                      Search and pin on map
                    </button>
                    <button type="button" aria-pressed={locationMethod === "url"} onClick={() => setLocationMethod("url")}>
                      <Link2 size={16} variant="Linear" aria-hidden="true" />
                      Paste Google Maps link
                    </button>
                  </div>

                  {locationMethod === "url" ? (
                    <div className="field">
                      <label className="field-label" htmlFor="dp-maps-url">Google Maps link</label>
                      <input
                        id="dp-maps-url"
                        type="text"
                        className="input"
                        placeholder="e.g. https://maps.app.goo.gl/... or https://google.com/maps?q=12.9,77.5..."
                        value={googleMapsUrl}
                        onChange={(e) => handleGoogleMapsUrlChange(e.target.value)}
                      />
                    </div>
                  ) : (
                    <div className="dp-map-search">
                      <input
                        type="text"
                        className="input"
                        placeholder="Search landmark, locality or city"
                        aria-label="Search the map"
                        value={mapSearchQuery}
                        onChange={(e) => setMapSearchQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleMapSearch(e); }}
                      />
                      <button type="button" className="btn-secondary" onClick={handleMapSearch}>
                        Search
                      </button>
                    </div>
                  )}

                  {locationMethod === "map" && (
                    <>
                      <div id="dealer-admin-map" className="dp-map" />
                      <p className="dp-map-coords">
                        <Location size={14} variant="Linear" aria-hidden="true" />
                        Pinned at {mapLat.toFixed(6)}, {mapLng.toFixed(6)}. Address fields fill in automatically.
                      </p>
                    </>
                  )}
                </section>

                <div className="field">
                  <label className="field-label" htmlFor="dp-address">Address</label>
                  <input
                    id="dp-address"
                    type="text"
                    className="input"
                    placeholder="e.g. Suite 201, 100 Feet Road, Indiranagar"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    required
                  />
                </div>

                <div className="dp-field-row">
                  <div className="field">
                    <label className="field-label" htmlFor="dp-locality">Locality</label>
                    <input
                      id="dp-locality"
                      type="text"
                      className="input"
                      placeholder="e.g. Indiranagar"
                      value={locality}
                      onChange={(e) => setLocality(e.target.value)}
                      required
                    />
                  </div>
                  <div className="field">
                    <label className="field-label" htmlFor="dp-city">City</label>
                    <input
                      id="dp-city"
                      type="text"
                      className="input"
                      placeholder="e.g. Bengaluru"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSaving}>
                  {isSaving && <span className="btn-spinner" aria-hidden="true" />}
                  {isSaving ? "Saving" : "Save dealer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
